import React, { useEffect, useMemo, useRef, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, API_BASE, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { substituteTokens } from "../../lib/templateTokens";
import { TemplatePicker } from "../../components/TemplatePicker";
import { MagicRewrite } from "../../components/MagicRewrite";
import { ISSUE_TAGS } from "../../lib/grievanceTags";
import {
  Plus, X, Ticket, CheckCircle, Phone, WhatsappLogo, EnvelopeSimple,
  MagnifyingGlass, DownloadSimple, FileCsv, FilePdf, Smiley,
  SmileyMeh, SmileyAngry, SmileyXEyes, User, Truck, PencilSimple,
  Microphone, Warning, Trash, Robot, Sparkle, CircleNotch, CaretDown, CaretUp,
} from "@phosphor-icons/react";

const STATUS_ALL = ["open", "in_progress", "resolved", "closed"];
const PRI_COLOR = {
  low:    "bg-[#E5E2D9] text-[#5C5A55]",
  medium: "bg-[#F9E4B7] text-[#7A5A11]",
  high:   "bg-[#F4C7B9] text-[#7A2E18]",
};
const ST_COLOR = {
  open:        "bg-[#F4C7B9] text-[#7A2E18]",
  in_progress: "bg-[#F9E4B7] text-[#7A5A11]",
  resolved:    "bg-[#D4E0D1] text-[#1A3317]",
  closed:      "bg-[#E5E2D9] text-[#5C5A55]",
};
const SOURCES = [
  { key: "call",         label: "Call",         icon: Phone },
  { key: "whatsapp",     label: "WhatsApp",     icon: WhatsappLogo },
  { key: "email",        label: "Email",        icon: EnvelopeSimple },
  { key: "contact_form", label: "Contact form", icon: User },
  { key: "walk_in",      label: "Walk-in",      icon: User },
  { key: "other",        label: "Other",        icon: User },
];
const MOODS = [
  { key: "normal",   label: "Normal",   icon: Smiley,     bg: "bg-[#D4E0D1] text-[#1A3317] border-[#2D5A27]" },
  { key: "cool",     label: "Cool",     icon: SmileyMeh,  bg: "bg-[#DDE7F1] text-[#1F3A57] border-[#1F3A57]" },
  { key: "angry",    label: "Angry",    icon: SmileyAngry,bg: "bg-[#F9E4B7] text-[#7A5A11] border-[#7A5A11]" },
  { key: "very_bad", label: "Very bad", icon: SmileyXEyes,bg: "bg-[#F4C7B9] text-[#7A2E18] border-[#7A2E18]" },
];

export const emptyForm = {
  customer_id: "", customer_name: "", phone: "", email: "",
  issue: "", priority: "medium", status: "open",
  source: "call", mood: "normal", assigned_to: [],
};

// Iter 98 — quick-note templates that click-to-fill the Issue textarea
const QUICK_NOTES = [
  "Need collection",
  "Need bill",
  "No pickup until further notice",
  "Emergency collection required",
  "Collection scheduled next week",
  "Bag delivery pending",
  "Complaint about staff",
];

export default function Tickets() {
  const { can, user } = useAuth();
  const canEdit = can(["superadmin", "admin", "manager", "service_coordinator", "office_staff", "office"]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [lookup, setLookup] = useState({ state: "idle", customer: null });
  const [filter, setFilter] = useState({ status: "", source: "", mood: "", tag: "", q: "" });
  const [exportPreset, setExportPreset] = useState("month");
  const [exporting, setExporting] = useState(false);
  // Iter 99 — allow ?tab=assigned deep link (used by the TicketBell toast)
  const initialTab = (typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("tab")) === "assigned"
    ? "assigned" : "all";
  const [tab, setTab] = useState(initialTab);
  const [users, setUsers] = useState([]);
  // Iter 109 — edit-ticket modal state (open with { ticket }, close with null)
  const [editing, setEditing] = useState(null);
  // Iter 120 — bulk-delete-closed (superadmin only)
  const isSuperadmin = user?.role === "superadmin";
  const [purgeMode, setPurgeMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [purgeModalOpen, setPurgeModalOpen] = useState(false);
  // Iter 138 — 60s ticker so per-row 24h/34h stagnation blink states
  // re-evaluate as time passes.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const load = async () => {
    if (tab === "assigned") {
      const t = await api.get("/tickets/assigned");
      setItems(t.data);
    } else {
      const t = await api.get("/tickets");
      setItems(t.data);
    }
  };
  useEffect(() => { load(); }, [tab]);   // reload when switching tabs

  // Iter 98 · updated Iter 100 — cache assignable staff. Excludes
  // superadmin (never assigned tickets) and any user marked inactive
  // (active === false).
  useEffect(() => {
    api.get("/auth/users").then(r => {
      const uu = (r.data || []).filter(u =>
        u.active !== false &&
        u.role !== "superadmin" &&
        ["office_staff", "office", "field_staff", "service_coordinator",
         "manager", "admin"].includes(u.role)
      );
      setUsers(uu);
    }).catch(() => setUsers([]));
  }, []);

  // -------- Phone-first lookup (debounced 400 ms) --------
  useEffect(() => {
    if (!open) return;
    const p = (form.phone || "").trim();
    if (p.length < 4) { setLookup({ state: "idle", customer: null }); return; }
    let alive = true;
    setLookup(s => ({ ...s, state: "searching" }));
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/tickets/lookup-customer?phone=${encodeURIComponent(p)}`);
        if (!alive) return;
        if (r.data.found) {
          setLookup({ state: "found", customer: r.data.customer });
          setForm(f => ({
            ...f,
            customer_id: r.data.customer.id,
            customer_name: r.data.customer.name,
            email: r.data.customer.email || f.email,
          }));
        } else {
          setLookup({ state: "walkin", customer: null });
          // Only clear customer_id — keep the typed name if user already entered one
          setForm(f => ({ ...f, customer_id: "" }));
        }
      } catch (_) { if (alive) setLookup({ state: "idle", customer: null }); }
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [form.phone, open]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, customer_id: form.customer_id || null };
    if (!payload.customer_id && !payload.customer_name.trim()) {
      toast.error("Enter a customer name (walk-in) or pick an existing customer via phone");
      return;
    }
    try {
      await api.post("/tickets", payload);
      toast.success("Ticket created");
      setOpen(false);
      setForm(emptyForm);
      setLookup({ state: "idle", customer: null });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateStatus = async (id, status) => {
    try { await api.put(`/tickets/${id}`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Iter 141 — Skill-based auto-assign
  const autoAssignTicket = async (id) => {
    try {
      const r = await api.post(`/crm/tickets/${id}/auto-assign`);
      const who = r.data?.assigned_to?.name || "an agent";
      toast.success(`Assigned to ${who} (AI score ${r.data?.assigned_to?.score})`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Iter 109 — save edits from the EditTicketModal (priority, status,
  // assignees, issue, resolution). Only sends fields that actually changed.
  const saveEdit = async (patch) => {
    if (!editing?.ticket) return;
    try {
      await api.put(`/tickets/${editing.ticket.id}`, patch);
      toast.success("Ticket updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Iter 120 — bulk-delete-closed with password confirmation.
  const confirmPurge = async (password) => {
    try {
      const r = await api.post("/tickets/bulk-delete", {
        ticket_ids: selectedIds,
        password,
      });
      const msg = `Deleted ${r.data.deleted}. ${r.data.skipped?.length
        ? `Skipped ${r.data.skipped.length} (not closed).` : ""}`;
      toast.success(msg);
      // Iter 121 — auto-download the PDF snapshot of what we just archived
      if (r.data.deleted_ids?.length) {
        try {
          const token = localStorage.getItem("rescigo_token") || "";
          const ids = r.data.deleted_ids.join(",");
          const pdfResp = await fetch(
            `${API_BASE}/tickets/archive/pdf?ids=${encodeURIComponent(ids)}`,
            { headers: token ? { Authorization: `Bearer ${token}` } : {} },
          );
          if (pdfResp.ok) {
            const blob = await pdfResp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const cd = pdfResp.headers.get("Content-Disposition") || "";
            const m = cd.match(/filename="?([^"]+)"?/);
            a.href = url;
            a.download = m ? m[1] : `ticket_purge_${Date.now()}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
          }
        } catch { /* PDF is best-effort — tickets are already archived */ }
      }
      setPurgeModalOpen(false);
      setSelectedIds([]);
      setPurgeMode(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportReport = async (fmt) => {
    setExporting(true);
    try {
      // We use fetch (not axios) so we can grab the raw blob and trigger a download.
      const qs = new URLSearchParams();
      qs.set("format", fmt);
      qs.set("preset", exportPreset);
      if (filter.status) qs.set("status", filter.status);
      if (filter.source) qs.set("source", filter.source);
      if (filter.mood)   qs.set("mood", filter.mood);
      const token = localStorage.getItem("rescigo_token");
      const url = `${API_BASE}/tickets/export?${qs.toString()}`;
      const r = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!r.ok) throw new Error(`Export failed (${r.status})`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `tickets_${exportPreset}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch (e) { toast.error(e?.message || "Export failed"); }
    finally { setExporting(false); }
  };

  const filtered = useMemo(() => {
    const q = filter.q.trim().toLowerCase();
    return items.filter(t => {
      if (filter.status && t.status !== filter.status) return false;
      if (filter.source && t.source !== filter.source) return false;
      if (filter.mood   && t.mood   !== filter.mood)   return false;
      // Iter 118 — issue-tag filter. Tags are stored on the ticket as
      // `tags: string[]`; grievance-app submissions populate this list.
      // As a graceful fallback for older tickets that don't have tags,
      // do a substring match against the issue text.
      if (filter.tag) {
        const tagLower = filter.tag.toLowerCase();
        const tagArr = (t.tags || []).map(x => String(x).toLowerCase());
        const inIssue = (t.issue || "").toLowerCase().includes(tagLower);
        if (!tagArr.includes(tagLower) && !inIssue) return false;
      }
      if (!q) return true;
      const hay = `${t.ticket_no} ${t.customer_name} ${t.phone} ${t.issue}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, filter]);

  return (
    <AppShell title="Complaint Tickets" subtitle="Module 4 · CRM"
      actions={canEdit && (
        <div className="flex items-center gap-2">
          <AutoReplyToggle/>
          <AutoAssignToggle/>
          <button data-testid="btn-new-ticket" onClick={() => setOpen(true)}
                  className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
            <Plus size={14} weight="bold"/>New ticket
          </button>
        </div>
      )}
    >
      {/* Iter 98 — sub-tabs: All Tickets / Assigned to me */}
      <div className="mb-4 flex items-center gap-1 border-b border-[#E5E2D9]">
        {[
          { key: "all",      label: "All Tickets" },
          { key: "assigned", label: "Assigned Tasks" },
        ].map(t => (
          <button key={t.key}
                  data-testid={`tkt-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`h-10 px-4 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    tab === t.key
                      ? "border-[#2D5A27] text-[#2D5A27]"
                      : "border-transparent text-[#5C5A55] hover:text-[#2D5A27]"
                  }`}>{t.label}
                  {t.key === "assigned" && tab !== "assigned" && (
                    <span className="ml-1 text-[9px] text-[#8A8780]">(mine)</span>
                  )}
          </button>
        ))}
      </div>
      {/* Filter + export toolbar */}
      <div className="bg-white border border-[#E5E2D9] rounded-md p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1">Search</label>
          <div className="relative">
            <MagnifyingGlass size={13} className="absolute left-2.5 top-2.5 text-[#8A8780]"/>
            <input value={filter.q}
                   onChange={e => setFilter(f => ({...f, q: e.target.value}))}
                   placeholder="Ticket no / customer / phone / issue"
                   data-testid="tkt-search"
                   className="w-full h-9 pl-8 pr-3 border border-[#E5E2D9] rounded-md text-sm"/>
          </div>
        </div>
        <Sel label="Status" value={filter.status} onChange={v => setFilter(f => ({...f, status: v}))}
             options={[["", "All"], ...STATUS_ALL.map(s => [s, s])]}/>
        <Sel label="Source" value={filter.source} onChange={v => setFilter(f => ({...f, source: v}))}
             options={[["", "All"], ...SOURCES.map(s => [s.key, s.label])]}/>
        <Sel label="Mood" value={filter.mood} onChange={v => setFilter(f => ({...f, mood: v}))}
             options={[["", "All"], ...MOODS.map(m => [m.key, m.label])]}/>
        {/* Export */}
        <div className="flex items-end gap-1">
          <Sel label="Range" value={exportPreset} onChange={setExportPreset}
               options={[["today","Today"],["yesterday","Yesterday"],["week","Last 7 days"],
                        ["month","This month"],["last_30","Last 30 days"],["","All"]]}/>
          <button onClick={() => exportReport("csv")} disabled={exporting}
                  data-testid="tkt-export-csv"
                  className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2] disabled:opacity-60">
            <FileCsv size={13} weight="fill"/>CSV
          </button>
          <button onClick={() => exportReport("pdf")} disabled={exporting}
                  data-testid="tkt-export-pdf"
                  className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2] disabled:opacity-60">
            <FilePdf size={13} weight="fill"/>PDF
          </button>
        </div>
      </div>

      {/* Iter 120 — Superadmin bulk-delete-closed action ribbon */}
      {isSuperadmin && (
        <div className="bg-[#FDECEC] border border-[#EFC7B6] rounded-md p-3 mb-4 flex flex-wrap items-center gap-2"
             data-testid="tkt-purge-ribbon">
          <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#7A2E18]">
            Superadmin · Purge closed tickets
          </div>
          <a href="/crm/tickets/archive"
             data-testid="tkt-archive-link"
             className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#5C5A55] hover:text-[#1A1A1A] underline underline-offset-2">
            View archive →
          </a>
          <a href="/admin/audit/ticket-purges"
             data-testid="tkt-audit-link"
             className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#5C5A55] hover:text-[#1A1A1A] underline underline-offset-2">
            Audit log →
          </a>
          {!purgeMode ? (
            <button onClick={() => { setPurgeMode(true); setSelectedIds([]); }}
                    data-testid="tkt-purge-enable"
                    className="h-8 px-3 rounded-md bg-[#7A2E18] hover:bg-[#5A2013] text-white text-[11px] font-semibold ml-auto">
              Enable select mode
            </button>
          ) : (
            <>
              <span className="text-[10px] text-[#5C5A55]">
                {selectedIds.length} selected · only closed / resolved tickets are eligible
              </span>
              <div className="flex items-center gap-2 ml-auto">
                <button onClick={() => {
                          // select every visible closed/resolved ticket
                          const eligible = filtered
                            .filter(t => t.status === "closed" || t.status === "resolved")
                            .map(t => t.id);
                          setSelectedIds(eligible);
                        }}
                        data-testid="tkt-purge-select-all-closed"
                        className="h-8 px-3 rounded-md bg-white border border-[#E5E2D9] text-[11px] font-semibold hover:bg-[#F7F6F2]">
                  Select all closed
                </button>
                <button onClick={() => setSelectedIds([])}
                        data-testid="tkt-purge-clear"
                        className="h-8 px-3 rounded-md bg-white border border-[#E5E2D9] text-[11px] font-semibold hover:bg-[#F7F6F2]">
                  Clear
                </button>
                <button onClick={() => setPurgeModalOpen(true)}
                        disabled={selectedIds.length === 0}
                        data-testid="tkt-purge-open-modal"
                        className="h-8 px-3 rounded-md bg-[#7A2E18] hover:bg-[#5A2013] disabled:opacity-40 disabled:cursor-not-allowed text-white text-[11px] font-semibold inline-flex items-center gap-1">
                  Delete {selectedIds.length || ""} …
                </button>
                <button onClick={() => { setPurgeMode(false); setSelectedIds([]); }}
                        data-testid="tkt-purge-exit"
                        className="h-8 px-3 rounded-md bg-white border border-[#E5E2D9] text-[11px] font-semibold hover:bg-[#F7F6F2]">
                  Exit
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Iter 118 — Issue tag filter chips (matches customer app tags) */}
      <div className="bg-white border border-[#E5E2D9] rounded-md p-3 mb-4 flex flex-wrap items-center gap-1.5"
           data-testid="tkt-issue-tags">
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] pr-1">Issue</span>
        <button onClick={() => setFilter(f => ({ ...f, tag: "" }))}
                data-testid="tkt-tag-all"
                className={`h-7 px-2.5 rounded-full text-[11px] font-medium border ${
                  filter.tag === ""
                    ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                    : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:border-[#B7B3A6]"
                }`}>all</button>
        {ISSUE_TAGS.map(t => (
          <button key={t}
                  onClick={() => setFilter(f => ({ ...f, tag: t }))}
                  data-testid={`tkt-tag-${t.replace(/\s+/g,"-").toLowerCase()}`}
                  className={`h-7 px-2.5 rounded-full text-[11px] font-medium border ${
                    filter.tag === t
                      ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                      : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:border-[#B7B3A6]"
                  }`}>{t}</button>
        ))}
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="tickets-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-4 py-3">Ticket</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Mood</th>
              <th className="px-4 py-3">Issue</th>
              <th className="px-4 py-3">Priority</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Pending</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="px-6 py-14 text-center text-[#8A8780]">
                <Ticket size={26} className="inline-block mb-3 opacity-40"/><br/>No tickets.
              </td></tr>
            )}
            {filtered.map((t, i) => (
              <Row key={t.id} t={t} zebra={i % 2 === 0} canEdit={canEdit}
                    now={now}
                    onStatus={updateStatus}
                    onEdit={() => setEditing({ ticket: t })}
                    purgeMode={purgeMode && isSuperadmin}
                    selected={selectedIds.includes(t.id)}
                    onToggleSelect={() => setSelectedIds(prev =>
                      prev.includes(t.id) ? prev.filter(x => x !== t.id) : [...prev, t.id])}
                    onAutoAssign={autoAssignTicket}/>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <TicketFormModal
          form={form} setForm={setForm}
          lookup={lookup} setLookup={setLookup}
          users={users}
          onClose={() => { setOpen(false); setForm(emptyForm); setLookup({state:"idle",customer:null}); }}
          onSubmit={submit}
        />
      )}

      {editing?.ticket && (
        <EditTicketModal
          ticket={editing.ticket}
          users={users}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
        />
      )}

      {purgeModalOpen && (
        <PurgeConfirmModal
          count={selectedIds.length}
          onCancel={() => setPurgeModalOpen(false)}
          onConfirm={confirmPurge}
        />
      )}
    </AppShell>
  );
}

// -------- Purge Confirm Modal (Iter 120) --------
// Superadmin-only. Requires typing the CURRENT admin password to
// authorize the destructive bulk delete — the same password used to
// sign in. Any error from the backend surfaces inline so failed attempts
// don't leave the user guessing what went wrong.
function PurgeConfirmModal({ count, onCancel, onConfirm }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try { await onConfirm(password); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={onCancel}
         data-testid="tkt-purge-modal">
      <div className="bg-white rounded-md w-full max-w-md p-6"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <Warning size={22} weight="fill" className="text-[#7A2E18]"/>
          <div className="font-heading text-lg text-[#7A2E18]">
            Delete {count} closed ticket(s)?
          </div>
        </div>
        <p className="text-sm text-[#5C5A55] mb-4">
          This will <strong>permanently remove</strong> the selected closed
          / resolved tickets along with all their replies and voice notes.
          Only tickets already in a closed or resolved state will be
          deleted; open tickets in the selection are skipped.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block">
            Confirm your password
          </label>
          <input type="password"
                 value={password}
                 onChange={e => setPassword(e.target.value)}
                 autoFocus
                 required
                 placeholder="Superadmin password"
                 data-testid="tkt-purge-password"
                 className="w-full h-11 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onCancel}
                    disabled={busy}
                    data-testid="tkt-purge-cancel"
                    className="h-11 px-4 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
              Cancel
            </button>
            <button type="submit" disabled={busy || !password}
                    data-testid="tkt-purge-confirm"
                    className="flex-1 h-11 bg-[#7A2E18] hover:bg-[#5A2013] disabled:opacity-60 text-white rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2">
              <Trash size={14} weight="bold"/>
              {busy ? "Deleting…" : `Delete ${count} ticket(s)`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------- Row (with WhatsApp / Email quick-actions) --------
function Row({ t, zebra, canEdit, onStatus, onEdit, now,
                purgeMode, selected, onToggleSelect, onAutoAssign }) {
  const navigate = useNavigate();
  const digits = (t.phone || "").replace(/\D+/g, "");
  const waNo = digits.length === 10 ? `91${digits}` : digits;
  const msg = encodeURIComponent(
    `Hi ${t.customer_name || "there"}, this is Rescigo Health regarding your ticket ${t.ticket_no}. ` +
    `Current status: ${t.status.replace(/_/g, " ")}. ` +
    `Reason: ${t.issue || "—"}. We'll keep you posted. Thanks!`,
  );
  const emailSubj = encodeURIComponent(`Rescigo · Ticket ${t.ticket_no} update`);
  const emailBody = encodeURIComponent(
    `Hi ${t.customer_name || "there"},\n\n` +
    `This is a status update on your ticket ${t.ticket_no}.\n` +
    `Current status: ${t.status.replace(/_/g, " ")}\n` +
    `Issue: ${t.issue || "—"}\n\n` +
    `Regards,\nRescigo Health`,
  );
  const mood = MOODS.find(m => m.key === t.mood) || MOODS[0];
  const src  = SOURCES.find(s => s.key === t.source) || SOURCES[0];
  const SrcIcon = src.icon;
  const MoodIcon = mood.icon;
  const closed = t.status === "closed" || t.status === "resolved";
  // Iter 116 — row-level urgency blink: angry/very-bad mood OR high
  // priority AND still open. Stops the moment the ticket is closed.
  const hot = !closed && (
    t.mood === "angry" || t.mood === "very_bad" || t.priority === "high"
  );
  // Iter 138 — stagnation blink for tickets that sit unresolved. Uses
  // updated_at (falls back to created_at) as the "last touch" anchor.
  // 24-34h → yellow soft blink, >34h → orange strong blink. Ignored on
  // hot rows (which already blink red for urgency).
  let stale = "ok";
  if (!closed && !hot) {
    const anchor = t.updated_at || t.created_at;
    if (anchor) {
      const hrs = (now - new Date(anchor).getTime()) / 3600000;
      if (hrs >= 34) stale = "stale";
      else if (hrs >= 24) stale = "warn";
    }
  }
  const staleCls =
    stale === "stale" ? "lead-blink-stale" :
    stale === "warn"  ? "lead-blink-warn"  : "";
  return (
    <tr className={`${zebra ? "bg-white" : "bg-[#FAFAF7]"} ${hot ? "tkt-blink-row" : staleCls}`}
        data-testid={`tkt-row-${t.id}`}
        data-stale-level={stale}>
      <td className="px-4 py-3 font-mono font-medium">
        {purgeMode && (t.status === "closed" || t.status === "resolved") && (
          <input type="checkbox"
                 checked={!!selected}
                 onChange={() => onToggleSelect && onToggleSelect()}
                 onClick={(e) => e.stopPropagation()}
                 data-testid={`tkt-purge-check-${t.id}`}
                 className="mr-2 align-middle accent-[#7A2E18]"/>
        )}
        {canEdit ? (
          <button type="button"
                  onClick={() => onEdit && onEdit()}
                  data-testid={`tkt-open-${t.id}`}
                  title="Open & edit ticket"
                  className="text-[#2D5A27] hover:underline">
            {t.ticket_no}
          </button>
        ) : t.ticket_no}
        {t.is_walkin && (
          <div className="text-[9px] uppercase tracking-[0.16em] text-[#8A8780] mt-0.5">walk-in</div>
        )}
      </td>
      <td className="px-4 py-3">
        <div>{t.customer_name || "—"}</div>
        {t.phone && <div className="text-[11px] text-[#8A8780]">{t.phone}</div>}
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1 text-xs text-[#5C5A55]">
          <SrcIcon size={12} weight="fill"/>{src.label}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] font-semibold px-1.5 py-0.5 rounded border ${mood.bg}`}>
          <MoodIcon size={12} weight="fill"/>{mood.label}
        </span>
      </td>
      <td className="px-4 py-3 text-[#5C5A55] max-w-sm">
        <div className="truncate">{t.issue}</div>
        {t.assigned_to_names?.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1 items-center"
               data-testid={`tkt-row-assignees-${t.id}`}>
            {t.assigned_to_names.slice(0, 3).map((n, i) => (
              <span key={i} className="text-[9px] uppercase tracking-[0.1em] font-semibold px-1.5 rounded bg-[#F0F7EE] text-[#1A3317] border border-[#B5D5AC]">
                {n}
              </span>
            ))}
            {t.assigned_to_names.length > 3 && (
              <span className="text-[9px] text-[#8A8780]">+{t.assigned_to_names.length - 3}</span>
            )}
            {t.auto_assigned && (
              <span data-testid={`tkt-auto-assigned-${t.id}`}
                    title={`AI auto-assigned (score ${t.auto_assign_score ?? "?"})`}
                    className="text-[9px] uppercase tracking-[0.1em] font-semibold px-1.5 rounded bg-[#DDE7F1] text-[#0B2B66] border border-[#0B2B66]/40 inline-flex items-center gap-0.5">
                <Robot size={9} weight="fill"/>AI
              </span>
            )}
          </div>
        )}
        {(!t.assigned_to_names || t.assigned_to_names.length === 0) && (
          <button data-testid={`tkt-auto-assign-${t.id}`}
                  onClick={(e) => { e.stopPropagation(); onAutoAssign?.(t.id); }}
                  className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-0.5 rounded bg-[#DDE7F1] text-[#0B2B66] hover:bg-[#C1D2EC]">
            <Robot size={11} weight="fill"/>Auto-assign
          </button>
        )}
        <button data-testid={`tkt-make-task-${t.id}`} title="Make a task from this ticket"
                onClick={(e) => { e.stopPropagation(); navigate(`/crm/tasks?ticket=${t.id}`); }}
                className="mt-1 ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-0.5 rounded bg-[#FEF2F2] text-[#B91C1C] hover:bg-[#FEE2E2]">
          + Task
        </button>

      </td>
      <td className="px-4 py-3">
        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${PRI_COLOR[t.priority]}`}>
          {t.priority}
        </span>
        {t.sla_escalated_at && (
          <span
            className="ml-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded bg-[#8A2C2C] text-white"
            data-testid={`tkt-sla-badge-${t.id}`}
            title={`Auto-escalated after 48h inactivity on ${String(t.sla_escalated_at).replace('T',' ').slice(0,16)}`}
          >
            SLA
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${ST_COLOR[t.status]}`}>
          {t.status}
        </span>
      </td>
      <td className="px-4 py-3">
        {(() => {
          const h = t.hours_since_activity;
          if (h == null) return <span className="text-[11px] text-[#8A8780]">—</span>;
          const closed = ["closed", "resolved"].includes(t.status);
          const stale = !closed && h >= 24;
          const label = h < 1
            ? `${Math.max(0, Math.round(h * 60))}m`
            : h < 24
            ? `${Math.round(h)}h`
            : `${Math.floor(h / 24)}d ${Math.round(h % 24)}h`;
          const cls = closed
            ? "text-[#8A8780]"
            : stale
            ? "text-[#8A2C2C] font-semibold tkt-blink"
            : h >= 8
            ? "text-[#7A5A11] font-medium"
            : "text-[#5C5A55]";
          return (
            <span className={`text-[11px] font-mono ${cls}`}
                  data-testid={`tkt-pending-${t.id}`}
                  title={`Last activity ${(t.updated_at || t.created_at || "").replace("T", " ").slice(0, 16)}`}>
              {stale && <span className="mr-1">●</span>}
              {label}
            </span>
          );
        })()}
      </td>
      <td className="px-4 py-3 text-right text-xs whitespace-nowrap">
        {/* WhatsApp / email quick send */}
        {waNo && (
          <a href={`https://wa.me/${waNo}?text=${msg}`} target="_blank" rel="noreferrer"
              data-testid={`tkt-wa-${t.id}`}
              title={`WhatsApp ${t.customer_name || t.phone}`}
              className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-[#D4E0D1]/60 text-[#075E54] mr-1">
            <WhatsappLogo size={16} weight="fill"/>
          </a>
        )}
        {t.email && (
          <a href={`mailto:${t.email}?subject=${emailSubj}&body=${emailBody}`}
              data-testid={`tkt-email-${t.id}`}
              title={`Email ${t.email}`}
              className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-[#DDE7F1] text-[#1F3A57] mr-1">
            <EnvelopeSimple size={16} weight="fill"/>
          </a>
        )}
        {t.customer_id && (
          <button
              onClick={() => {
                const params = new URLSearchParams({
                  customer_id: t.customer_id,
                  description: `Follow-up on ticket ${t.ticket_no} · ${t.issue || ""}`,
                  priority: t.priority === "high" ? "HIGH"
                            : t.priority === "medium" ? "NORMAL" : "NORMAL",
                  source_ticket_id: t.id,
                });
                navigate(`/operations/service-requests/new?${params}`);
              }}
              data-testid={`tkt-create-sr-${t.id}`}
              title="Create Service Request for this customer"
              className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-[#EDF7EA] text-[#2D5A27] mr-1">
            <Truck size={16} weight="fill"/>
          </button>
        )}
        {canEdit && onEdit && (
          <button onClick={() => onEdit()}
                  data-testid={`tkt-edit-${t.id}`}
                  title="Edit ticket · assign, priority, status"
                  className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-[#F0EFEA] text-[#5C5A55] mr-1">
            <PencilSimple size={15} weight="bold"/>
          </button>
        )}
        {canEdit && STATUS_ALL.filter(s => s !== t.status).map(s => (
          <button key={s} onClick={() => onStatus(t.id, s)} data-testid={`tkt-${s}-${t.id}`}
                  className="text-[#2D5A27] mr-2">→ {s.replace(/_/g, " ")}</button>
        ))}
      </td>
    </tr>
  );
}

// -------- Small helpers --------
function Sel({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] block mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
              className="h-9 px-2 border border-[#E5E2D9] rounded-md text-sm bg-white">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

// -------- Modal --------
export function TicketFormModal({ form, setForm, lookup, setLookup, users, onClose, onSubmit, title = "New ticket" }) {
  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [facts, setFacts] = useState(null);

  // Fetch autocomplete once 6+ digits typed (debounced 300 ms)
  useEffect(() => {
    if (lookup.state === "found") return;
    const digits = (form.phone || "").replace(/\D+/g, "");
    if (digits.length < 6) { setSuggestions([]); setShowSug(false); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/tickets/customer-search?q=${encodeURIComponent(digits)}`);
        if (!alive) return;
        setSuggestions(r.data || []);
        setShowSug((r.data || []).length > 0);
      } catch { if (alive) setSuggestions([]); }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [form.phone, lookup.state]);

  // When customer_id changes, fetch quick facts
  useEffect(() => {
    if (!form.customer_id) { setFacts(null); return; }
    let alive = true;
    api.get(`/tickets/customer-facts/${form.customer_id}`)
      .then(r => { if (alive) setFacts(r.data); })
      .catch(() => { if (alive) setFacts(null); });
    return () => { alive = false; };
  }, [form.customer_id]);

  const pickCustomer = (c) => {
    setForm(f => ({
      ...f, customer_id: c.id, customer_name: c.name,
      phone: c.phone || f.phone, email: c.email || f.email,
    }));
    setLookup({ state: "found", customer: c });
    setShowSug(false);
    setSuggestions([]);
  };

  const appendNote = (note) => {
    setForm(f => {
      const cur = (f.issue || "").trim();
      const next = cur ? `${cur} · ${note}` : note;
      return { ...f, issue: next };
    });
  };

  const toggleAssignee = (uid) => {
    setForm(f => {
      const set = new Set(f.assigned_to || []);
      if (set.has(uid)) set.delete(uid); else set.add(uid);
      return { ...f, assigned_to: [...set] };
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-lg">{title}</div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4" data-testid="ticket-form">
          {/* Phone with autocomplete */}
          <div className="relative">
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">
              <Phone size={11} className="inline mr-0.5 -mt-0.5"/> Customer phone
            </label>
            <input data-testid="tkt-phone" placeholder="Type 6+ digits to search"
                    inputMode="tel"
                    value={form.phone}
                    onChange={e => {
                      setForm(f => ({ ...f, phone: e.target.value, customer_id: "" }));
                      setLookup({ state: "idle", customer: null });
                    }}
                    onFocus={() => suggestions.length > 0 && setShowSug(true)}
                    className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
            <LookupBanner lookup={lookup}/>

            {showSug && suggestions.length > 0 && (
              <div data-testid="tkt-phone-suggestions"
                   className="absolute z-10 mt-1 w-full bg-white border border-[#E5E2D9] rounded-md shadow-xl max-h-56 overflow-y-auto">
                {suggestions.map(c => (
                  <button key={c.id} type="button"
                          data-testid={`tkt-sug-${c.id}`}
                          onClick={() => pickCustomer(c)}
                          className="w-full text-left px-3 py-2 hover:bg-[#F0F7EE] border-b border-[#F0EEE6] last:border-0">
                    <div className="text-sm font-medium text-[#1A1A1A] flex items-center gap-1.5">
                      {c.name}
                      {c.customer_code && (
                        <span className="text-[9px] uppercase tracking-[0.1em] bg-[#F0EFEA] text-[#5C5A55] px-1 rounded">
                          {c.customer_code}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#8A8780] font-mono">
                      {c.phone}{c.lsgd_name && ` · ${c.lsgd_name}`}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Customer info tags — after selection */}
          {facts && lookup.state === "found" && (
            <CustomerFactChips facts={facts}/>
          )}

          {/* Name */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">
              Customer name {lookup.state === "walkin" && (
                <span className="ml-1 text-[9px] uppercase tracking-[0.16em] bg-[#F9E4B7] text-[#7A5A11] px-1 py-0.5 rounded">walk-in</span>
              )}
            </label>
            <input data-testid="tkt-name" placeholder="Customer name"
                    value={form.customer_name} required
                    disabled={lookup.state === "found"}
                    onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                    className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md disabled:bg-[#F7F6F2]"/>
          </div>

          {/* Email */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">
              Email <span className="text-[9px] normal-case text-[#8A8780]">optional</span>
            </label>
            <input placeholder="customer@example.com" type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
          </div>

          {/* Source chips */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1.5">Source</label>
            <div className="grid grid-cols-3 gap-1.5">
              {SOURCES.map(s => {
                const Icon = s.icon;
                const active = form.source === s.key;
                return (
                  <button key={s.key} type="button"
                          data-testid={`tkt-src-${s.key}`}
                          onClick={() => setForm(f => ({ ...f, source: s.key }))}
                          className={`h-9 px-2 rounded-md text-xs border-2 inline-flex items-center gap-1 justify-center transition-colors ${
                            active ? "border-[#2D5A27] bg-[#D4E0D1] text-[#1A3317]"
                                   : "border-[#E5E2D9] hover:border-[#B7B3A6] bg-white"
                          }`}>
                    <Icon size={13} weight={active ? "fill" : "regular"}/>{s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mood chips */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1.5">Customer mood</label>
            <div className="grid grid-cols-4 gap-1.5">
              {MOODS.map(m => {
                const Icon = m.icon;
                const active = form.mood === m.key;
                return (
                  <button key={m.key} type="button"
                          data-testid={`tkt-mood-${m.key}`}
                          onClick={() => setForm(f => ({ ...f, mood: m.key }))}
                          className={`h-14 rounded-md text-[10px] uppercase tracking-[0.14em] border-2 flex flex-col items-center justify-center gap-0.5 transition-colors ${
                            active ? m.bg : "border-[#E5E2D9] hover:border-[#B7B3A6] bg-white text-[#5C5A55]"
                          }`}>
                    <Icon size={18} weight={active ? "fill" : "regular"}/>
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Issue with quick-note chips */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">Issue</label>
            <div className="flex flex-wrap gap-1 mb-2" data-testid="tkt-quick-notes">
              {QUICK_NOTES.map(n => (
                <button key={n} type="button"
                        data-testid={`tkt-quicknote-${n.split(" ")[0].toLowerCase()}`}
                        onClick={() => appendNote(n)}
                        className="h-6 px-2 rounded-full bg-[#F0F7EE] hover:bg-[#DDECD9] text-[#1A3317] text-[10px] font-medium border border-[#B5D5AC]">
                  + {n}
                </button>
              ))}
            </div>
            <textarea data-testid="tkt-issue" required placeholder="Describe the issue"
                       value={form.issue}
                       onChange={e => setForm(f => ({ ...f, issue: e.target.value }))}
                       className="w-full min-h-[90px] p-3 border border-[#E5E2D9] rounded-md text-sm"/>
          </div>

          {/* Assign to (multi-select office + field staff) */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1.5">
              Assign to <span className="text-[9px] normal-case text-[#8A8780]">multi-select · optional</span>
            </label>
            <div data-testid="tkt-assignees"
                 className="max-h-40 overflow-y-auto border border-[#E5E2D9] rounded-md divide-y divide-[#F0EEE6]">
              {users.length === 0 && (
                <div className="p-3 text-[11px] text-[#8A8780] italic">No assignable staff found.</div>
              )}
              {users.map(u => {
                const checked = form.assigned_to?.includes(u.id);
                return (
                  <label key={u.id}
                         data-testid={`tkt-assignee-${u.id}`}
                         className="flex items-center gap-2 px-3 py-1.5 hover:bg-[#F7F6F2] cursor-pointer">
                    <input type="checkbox" checked={!!checked}
                            onChange={() => toggleAssignee(u.id)}
                            data-testid={`tkt-assignee-check-${u.id}`}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{u.name}</div>
                      <div className="text-[10px] text-[#8A8780]">
                        {u.role.replace(/_/g, " ")}{u.email && ` · ${u.email}`}
                      </div>
                    </div>
                    <span className={`text-[9px] uppercase font-semibold px-1.5 rounded ${
                      u.role.includes("field") ? "bg-[#DDE7F1] text-[#1F3A57]"
                                                : "bg-[#F0F7EE] text-[#1A3317]"}`}>
                      {u.role.includes("field") ? "field" : "office"}
                    </span>
                  </label>
                );
              })}
            </div>
            {form.assigned_to?.length > 0 && (
              <div className="mt-1 text-[10px] text-[#5C5A55]"
                   data-testid="tkt-assignee-count">
                {form.assigned_to.length} assignee(s) selected
              </div>
            )}
          </div>

          {/* Priority */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">Priority</label>
            <select value={form.priority}
                    onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                    className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
              {["low", "medium", "high"].map(p => <option key={p}>{p}</option>)}
            </select>
          </div>

          <button data-testid="tkt-save" type="submit"
                  className="w-full h-11 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
            <CheckCircle size={14} weight="fill"/>Create ticket
          </button>
        </form>
      </div>
    </div>
  );
}

// -------- Edit Ticket Modal (Iter 109 · timeline Iter 110) --------
// Open, review and edit any ticket: change priority, status, multi-assignees,
// the issue text, and add a resolution note. Read-only customer identity
// panel keeps context; only the fields the operator changed are PUT.
export function EditTicketModal({ ticket, users, onClose, onSave }) {
  const [priority, setPriority] = useState(ticket.priority || "medium");
  const [status, setStatus]     = useState(ticket.status || "open");
  const [issue, setIssue]       = useState(ticket.issue || "");
  const [resolution, setResolution] = useState(ticket.resolution || "");
  const [assigned, setAssigned] = useState(
    Array.isArray(ticket.assigned_to) ? [...ticket.assigned_to] : []
  );
  const [saving, setSaving] = useState(false);
  const [activity, setActivity] = useState(null);
  // Iter 115 — voice grievance metadata (null=loading, {has_voice:false}=none)
  const [voice, setVoice] = useState(null);
  // Iter 116 — reply thread state
  const [replies, setReplies] = useState([]);
  const [replyDraft, setReplyDraft] = useState("");
  const [sending, setSending] = useState(false);
  // Iter 133 — AI triage analysis
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showSuperReport, setShowSuperReport] = useState(false);
  // Iter 138 — Language variant of the AI-suggested reply. Defaults to
  // English; Malayalam / bilingual are fetched from the backend on
  // demand and cached on the analysis doc so subsequent switches are
  // instant.
  const [replyLang, setReplyLang] = useState("en");
  const [replyLangLoading, setReplyLangLoading] = useState(false);
  const { user: currentUser } = useAuth();
  const replyRef = useRef(null);

  // Fetch the activity log + timing summary each time the modal opens
  // (or after a save happens — bumping `saving` back to false triggers).
  useEffect(() => {
    let alive = true;
    api.get(`/tickets/${ticket.id}/activity`)
      .then(r => { if (alive) setActivity(r.data); })
      .catch(() => { if (alive) setActivity(null); });
    api.get(`/tickets/${ticket.id}/voice/meta`)
      .then(r => { if (alive) setVoice(r.data); })
      .catch(() => { if (alive) setVoice({ has_voice: false }); });
    api.get(`/tickets/${ticket.id}/replies`)
      .then(r => { if (alive) setReplies(r.data); })
      .catch(() => { if (alive) setReplies([]); });
    // Iter 133 — poll AI analysis until it's ready (max ~40s of polling)
    let pollCount = 0;
    const loadAi = async () => {
      try {
        const r = await api.get(`/tickets/${ticket.id}/ai-analysis`);
        if (!alive) return;
        setAiAnalysis(r.data);
        if (r.data?.status !== "ready" && r.data?.status !== "failed" && pollCount < 8) {
          pollCount += 1;
          setTimeout(loadAi, 5000);
        }
      } catch { /* ignore */ }
    };
    loadAi();
    return () => { alive = false; };
  }, [ticket.id, saving]);

  const sendReply = async () => {
    const txt = replyDraft.trim();
    if (!txt || sending) return;
    setSending(true);
    try {
      const r = await api.post(`/tickets/${ticket.id}/reply`, { body: txt });
      setReplies(prev => [...prev, r.data]);
      setReplyDraft("");
      toast.success("Reply sent");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  // Iter 133 — one-click send of the AI's suggested reply. If admin
  // edited the draft (populated it into replyDraft via the "Edit" button)
  // the current draft is sent, otherwise the raw suggestion.
  const approveAiReply = async () => {
    if (sending) return;
    setSending(true);
    try {
      const active = activeSuggestedReply();
      const body = replyDraft.trim() || active || aiAnalysis?.suggested_reply || "";
      const r = await api.post(`/tickets/${ticket.id}/ai-approve-reply`, { body });
      setReplies(prev => [...prev, r.data]);
      setReplyDraft("");
      setAiAnalysis(prev => prev ? { ...prev, sent_as_reply: true } : prev);
      toast.success("AI reply sent");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const editAiReply = () => {
    const txt = activeSuggestedReply() || aiAnalysis?.suggested_reply;
    if (!txt) return;
    setReplyDraft(txt);
    requestAnimationFrame(() => replyRef.current?.focus());
  };

  const regenerateAi = async () => {
    if (aiLoading) return;
    setAiLoading(true);
    try {
      await api.post(`/tickets/${ticket.id}/ai-regenerate`);
      setAiAnalysis(prev => ({ ...(prev || {}), status: "pending" }));
      // Reset language variants — they're derived from the English text.
      setReplyLang("en");
      toast.success("Regenerating AI analysis…");
      // Poll again
      let attempts = 0;
      const poll = async () => {
        try {
          const r = await api.get(`/tickets/${ticket.id}/ai-analysis`);
          setAiAnalysis(r.data);
          if (r.data?.status === "ready" || r.data?.status === "failed") return;
          attempts += 1;
          if (attempts < 8) setTimeout(poll, 5000);
        } catch { /* ignore */ }
      };
      setTimeout(poll, 5000);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiLoading(false); }
  };

  // Iter 138 — Fetch Malayalam / bilingual variant on demand.
  const switchReplyLang = async (lang) => {
    if (replyLang === lang || replyLangLoading) return;
    if (lang === "en") { setReplyLang("en"); return; }
    // ml or both — check cache first
    const cached = lang === "ml" ? aiAnalysis?.suggested_reply_ml
                                  : aiAnalysis?.suggested_reply_bilingual;
    if (cached) { setReplyLang(lang); return; }
    setReplyLangLoading(true);
    try {
      const r = await api.post(`/tickets/${ticket.id}/ai-translate-reply`,
                                 { language: lang });
      const field = lang === "ml" ? "suggested_reply_ml" : "suggested_reply_bilingual";
      setAiAnalysis(prev => prev ? { ...prev, [field]: r.data.text } : prev);
      setReplyLang(lang);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReplyLangLoading(false); }
  };

  const activeSuggestedReply = () => {
    if (replyLang === "ml") return aiAnalysis?.suggested_reply_ml || "";
    if (replyLang === "both") return aiAnalysis?.suggested_reply_bilingual || "";
    return aiAnalysis?.suggested_reply || "";
  };

  const insertTemplate = (tpl) => {
    const ctx = {
      customer_name:  ticket.customer_name,
      customer_phone: ticket.phone,
      customer_email: ticket.email,
      ticket_no:      ticket.ticket_no,
      agent_name:     currentUser?.name || currentUser?.email,
    };
    const filled = substituteTokens(tpl.body, ctx);
    setReplyDraft(prev => prev && prev.trim() ? `${prev.trim()}\n\n${filled}` : filled);
    requestAnimationFrame(() => replyRef.current?.focus());
    api.post(`/reply-templates/${tpl.id}/use`).catch(() => {});
  };

  const toggle = (uid) => {
    setAssigned(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
  };

  const submit = async (e) => {
    e.preventDefault();
    // Diff against the original — only send what changed to avoid churn on
    // the audit log and keep updated_at moves meaningful.
    const patch = {};
    if (priority !== ticket.priority) patch.priority = priority;
    if (status   !== ticket.status)   patch.status   = status;
    if (issue.trim() !== (ticket.issue || "").trim()) patch.issue = issue.trim();
    if (resolution !== (ticket.resolution || "")) patch.resolution = resolution;
    const origAssigned = (ticket.assigned_to || []).slice().sort().join(",");
    const nextAssigned = assigned.slice().sort().join(",");
    if (origAssigned !== nextAssigned) patch.assigned_to = assigned;
    if (Object.keys(patch).length === 0) {
      toast.info("Nothing to save");
      return;
    }
    setSaving(true);
    try { await onSave(patch); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="edit-ticket-modal">
      <div className="bg-white rounded-md w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-heading text-lg">Edit ticket</div>
            <div className="text-[11px] text-[#8A8780] font-mono mt-0.5">{ticket.ticket_no}</div>
          </div>
          <button onClick={onClose}
                  data-testid="edit-ticket-close"
                  className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
        </div>

        {/* Customer identity — read-only */}
        <div className="mb-4 p-3 bg-[#F7F6F2] border border-[#E5E2D9] rounded-md text-sm">
          <div className="font-medium">{ticket.customer_name || "Walk-in"}</div>
          {ticket.phone && <div className="text-[11px] text-[#5C5A55] font-mono">{ticket.phone}</div>}
          {ticket.email && <div className="text-[11px] text-[#5C5A55]">{ticket.email}</div>}
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#8A8780] mt-1">
            {(ticket.source || "").replace(/_/g," ")} · mood: {ticket.mood || "normal"}
          </div>
        </div>

        {/* Iter 110 — SLA / Timing chips */}
        {activity?.timing && (
          <TimingChips t={activity.timing}/>
        )}

        {/* Iter 115 — Voice grievance player + download */}
        {voice?.has_voice && (
          <VoicePlayer ticketId={ticket.id} meta={voice}/>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="edit-ticket-form">
          {/* Priority */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1.5">Priority</label>
            <div className="grid grid-cols-3 gap-1.5">
              {["low", "medium", "high"].map(p => {
                const active = priority === p;
                return (
                  <button key={p} type="button"
                          data-testid={`edit-tkt-pri-${p}`}
                          onClick={() => setPriority(p)}
                          className={`h-9 rounded-md text-xs uppercase tracking-[0.16em] font-semibold border-2 transition-colors ${
                            active
                              ? (p === "high"   ? "border-[#7A2E18] bg-[#F4C7B9] text-[#7A2E18]"
                              :  p === "medium" ? "border-[#7A5A11] bg-[#F9E4B7] text-[#7A5A11]"
                              :                   "border-[#5C5A55] bg-[#E5E2D9] text-[#5C5A55]")
                              : "border-[#E5E2D9] hover:border-[#B7B3A6] bg-white text-[#5C5A55]"
                          }`}>{p}</button>
                );
              })}
            </div>
            {ticket.sla_escalated_at && (
              <div className="mt-1 text-[10px] text-[#7A2E18]"
                   data-testid="edit-tkt-sla-note">
                Auto-escalated to <strong>high</strong> after 48h of inactivity.
              </div>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1.5">Status</label>
            <div className="grid grid-cols-4 gap-1.5">
              {STATUS_ALL.map(s => {
                const active = status === s;
                return (
                  <button key={s} type="button"
                          data-testid={`edit-tkt-st-${s}`}
                          onClick={() => setStatus(s)}
                          className={`h-9 rounded-md text-[10px] uppercase tracking-[0.14em] font-semibold border-2 transition-colors ${
                            active ? `${ST_COLOR[s]} border-transparent`
                                   : "border-[#E5E2D9] hover:border-[#B7B3A6] bg-white text-[#5C5A55]"
                          }`}>{s.replace(/_/g," ")}</button>
                );
              })}
            </div>
          </div>

          {/* Assignees */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1.5">
              Assign / Reassign <span className="text-[9px] normal-case text-[#8A8780]">multi-select</span>
            </label>
            <div data-testid="edit-tkt-assignees"
                 className="max-h-40 overflow-y-auto border border-[#E5E2D9] rounded-md divide-y divide-[#F0EEE6]">
              {(!users || users.length === 0) && (
                <div className="p-3 text-[11px] text-[#8A8780] italic">No assignable staff found.</div>
              )}
              {users.map(u => {
                const checked = assigned.includes(u.id);
                return (
                  <label key={u.id}
                         data-testid={`edit-tkt-assignee-${u.id}`}
                         className="flex items-center gap-2 px-3 py-1.5 hover:bg-[#F7F6F2] cursor-pointer">
                    <input type="checkbox" checked={checked}
                           onChange={() => toggle(u.id)}
                           data-testid={`edit-tkt-assignee-check-${u.id}`}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{u.name}</div>
                      <div className="text-[10px] text-[#8A8780]">
                        {u.role.replace(/_/g, " ")}{u.email && ` · ${u.email}`}
                      </div>
                    </div>
                    <span className={`text-[9px] uppercase font-semibold px-1.5 rounded ${
                      u.role.includes("field") ? "bg-[#DDE7F1] text-[#1F3A57]"
                                                : "bg-[#F0F7EE] text-[#1A3317]"}`}>
                      {u.role.includes("field") ? "field" : "office"}
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="mt-1 text-[10px] text-[#5C5A55]"
                 data-testid="edit-tkt-assignee-count">
              {assigned.length} assignee(s) selected
            </div>
          </div>

          {/* Issue */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">Issue</label>
            <textarea data-testid="edit-tkt-issue"
                       value={issue}
                       onChange={e => setIssue(e.target.value)}
                       className="w-full min-h-[80px] p-3 border border-[#E5E2D9] rounded-md text-sm"/>
          </div>

          {/* Resolution */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">
              Resolution note <span className="text-[9px] normal-case text-[#8A8780]">optional</span>
            </label>
            <textarea data-testid="edit-tkt-resolution"
                       placeholder="What was done to resolve this?"
                       value={resolution}
                       onChange={e => setResolution(e.target.value)}
                       className="w-full min-h-[70px] p-3 border border-[#E5E2D9] rounded-md text-sm"/>
          </div>

          {/* Iter 110 — Activity timeline (read-only) */}
          {activity?.activity && (
            <ActivityTimeline entries={activity.activity}/>
          )}

          {/* Iter 133 — AI Analysis panel */}
          <AIAnalysisPanel
            analysis={aiAnalysis}
            isSuperadmin={currentUser?.role === "superadmin"}
            showSuperReport={showSuperReport}
            setShowSuperReport={setShowSuperReport}
            onRegenerate={regenerateAi}
            regenerating={aiLoading}/>

          {/* Iter 133 — AI suggested reply box (yellow) — only admin/superadmin
              can approve/send. Other CRM editors see it read-only.
              Iter 138 — Language selector: EN (default) / ML / Both. */}
          {aiAnalysis?.status === "ready" &&
           aiAnalysis?.suggested_reply &&
           !aiAnalysis?.sent_as_reply && (
            <div className="rounded-md border-2 border-[#F5C518] bg-[#FFF9E3] p-3 space-y-2"
                 data-testid="ai-suggested-reply-box">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#7A5A11] font-semibold flex-wrap">
                <Robot size={12} weight="fill"/>Rigo AI · Suggested reply
                <div className="ml-auto inline-flex rounded-md border border-[#E5C978] overflow-hidden"
                     data-testid="ai-reply-lang-picker">
                  {[
                    { key: "en",   label: "EN" },
                    { key: "ml",   label: "ML" },
                    { key: "both", label: "EN+ML" },
                  ].map(o => (
                    <button key={o.key} type="button"
                            onClick={() => switchReplyLang(o.key)}
                            disabled={replyLangLoading}
                            data-testid={`ai-reply-lang-${o.key}`}
                            className={`px-2 py-0.5 text-[10px] tracking-normal ${
                              replyLang === o.key
                                ? "bg-[#7A5A11] text-white font-semibold"
                                : "bg-[#FFF9E3] text-[#7A5A11] hover:bg-[#F9E4B7]"
                            }`}>
                      {replyLangLoading && replyLang !== o.key && o.key !== "en" ? "…" : o.label}
                    </button>
                  ))}
                </div>
                {aiAnalysis.model && (
                  <span className="normal-case tracking-normal text-[9px] text-[#8A8780] font-normal">
                    {aiAnalysis.model}
                  </span>
                )}
              </div>
              <div className="text-[13px] whitespace-pre-wrap leading-snug text-[#1A1A1A]"
                   data-testid="ai-suggested-reply-text">
                {activeSuggestedReply() || aiAnalysis.suggested_reply}
              </div>
              {replyLang !== "en" && !activeSuggestedReply() && (
                <div className="text-[10px] italic text-[#7A5A11]">
                  Translating… showing English while we fetch.
                </div>
              )}
              {["admin", "superadmin"].includes(currentUser?.role) ? (
                <div className="flex gap-2">
                  <button type="button" onClick={approveAiReply} disabled={sending}
                          data-testid="ai-approve-send"
                          className="h-9 px-3 rounded-md bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white text-[11px] font-semibold inline-flex items-center gap-1">
                    <CheckCircle size={12} weight="fill"/>Send as reply
                  </button>
                  <button type="button" onClick={editAiReply}
                          data-testid="ai-edit"
                          className="h-9 px-3 rounded-md border border-[#7A5A11] text-[#7A5A11] hover:bg-[#F9E4B7] text-[11px] font-semibold">
                    Edit before sending
                  </button>
                  <button type="button" onClick={regenerateAi} disabled={aiLoading}
                          data-testid="ai-regenerate-inline"
                          className="ml-auto h-9 px-3 rounded-md text-[#5C5A55] hover:bg-[#F5EAB0] text-[11px]">
                    {aiLoading ? "Regenerating…" : "Regenerate"}
                  </button>
                </div>
              ) : (
                <div className="text-[10px] text-[#7A5A11] italic"
                     data-testid="ai-suggested-reply-readonly">
                  Only admin / superadmin can approve and send this suggestion.
                </div>
              )}
            </div>
          )}

          {/* Iter 116 — In-modal reply thread */}
          <ReplyPane
            replies={replies}
            draft={replyDraft}
            setDraft={setReplyDraft}
            onSend={sendReply}
            sending={sending}
            onTemplate={insertTemplate}
            inputRef={replyRef}/>

          <div className="flex items-center gap-2 pt-1">
            <button type="button" onClick={onClose}
                    data-testid="edit-tkt-cancel"
                    className="h-11 px-4 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
              Cancel
            </button>
            <button data-testid="edit-tkt-save" type="submit" disabled={saving}
                    className="flex-1 h-11 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
              <CheckCircle size={14} weight="fill"/>{saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------- Reply Pane (Iter 116) --------
// In-modal chat: history of past staff replies + composer with the
// reply-template picker. Sends to `POST /tickets/{tid}/reply` which
// also appends an entry to the ticket's activity timeline.
function ReplyPane({ replies, draft, setDraft, onSend, sending, onTemplate, inputRef }) {
  return (
    <div className="border-t border-[#E5E2D9] pt-3" data-testid="edit-tkt-reply-pane">
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">
        Reply to customer
        <span className="ml-1 text-[9px] normal-case text-[#8A8780]">
          · {replies.length} sent
        </span>
      </div>
      {replies.length > 0 && (
        <div className="max-h-56 overflow-y-auto space-y-1.5 mb-2 pr-1"
             data-testid="edit-tkt-reply-list">
          {replies.map(r => (
            <div key={r.id} className="flex justify-end"
                 data-testid={`edit-tkt-reply-${r.id}`}>
              <div className="max-w-[85%] rounded-2xl rounded-br-none bg-[#2D5A27] text-white px-3 py-1.5 text-[13px]">
                <div className="whitespace-pre-wrap leading-snug">{r.body}</div>
                <div className="text-[9px] mt-0.5 text-white/70">
                  {r.author_name || "Staff"} · {new Date(r.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <textarea
          ref={inputRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onSend(); } }}
          placeholder="Write a reply to the customer… (Ctrl/Cmd+Enter to send)"
          rows={5}
          data-testid="edit-tkt-reply-input"
          className="flex-1 min-h-[140px] max-h-[280px] px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm resize-y focus:border-[#2D5A27] outline-none"
        />
        <div className="flex flex-col gap-2 shrink-0">
          <TemplatePicker onPick={onTemplate}/>
          <MagicRewrite draft={draft}
                        onAccept={(t) => setDraft(t)}
                        testidPrefix="ticket"
                        size="sm"/>
        </div>
        <button type="button" onClick={onSend} disabled={sending || !draft.trim()}
                data-testid="edit-tkt-reply-send"
                className="h-10 px-4 rounded-full bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white text-sm font-medium flex items-center gap-2 shrink-0">
          {sending ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
}

// -------- Voice grievance player (Iter 115) --------
// Streams the customer's voice note through an authed backend proxy
// (browsers can't attach an Authorization header to <audio src>, so we
// fetch as blob → objectURL). Also offers a one-click download that
// hits the same endpoint with `?download=1`. Auto-expiry badge shows
// how many days are left before Mongo's TTL sweep purges the file.
function VoicePlayer({ ticketId, meta }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAudio = async () => {
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem("rescigo_token") || "";
      const r = await fetch(`${API_BASE}/tickets/${ticketId}/voice`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`Voice fetch failed (${r.status})`);
      const b = await r.blob();
      const url = URL.createObjectURL(b);
      setBlobUrl(url);
    } catch (e) {
      setError(e?.message || "Could not load voice");
      toast.error(e?.message || "Could not load voice");
    } finally { setLoading(false); }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("rescigo_token") || "";
      const r = await fetch(`${API_BASE}/tickets/${ticketId}/voice?download=1`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`Download failed (${r.status})`);
      const b = await r.blob();
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.href = url;
      a.download = m ? m[1] : `grievance_voice_${ticketId}.webm`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Voice note downloaded");
    } catch (e) { toast.error(e?.message || "Download failed"); }
    finally { setDownloading(false); }
  };

  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  // Days-left label for the auto-delete badge
  const daysLeft = (() => {
    if (!meta?.expires_at) return null;
    const ms = new Date(meta.expires_at).getTime() - Date.now();
    if (ms <= 0) return "expiring soon";
    const d = Math.floor(ms / (24 * 3600 * 1000));
    const h = Math.floor((ms % (24 * 3600 * 1000)) / (3600 * 1000));
    if (d >= 1) return `${d}d ${h}h left`;
    return `${h}h left`;
  })();

  return (
    <div className="mb-4 p-3 rounded-md border border-[#D4E0D1] bg-[#F0F7EE]"
         data-testid="edit-tkt-voice">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Microphone size={14} weight="fill" className="text-[#2D5A27]"/>
          <span className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#1A3317]">
            Voice grievance
          </span>
          {daysLeft && (
            <span
              className="text-[9px] uppercase tracking-[0.14em] font-semibold px-1.5 py-0.5 rounded bg-white text-[#5C5A55] border border-[#E5E2D9]"
              title={`Auto-deletes on ${new Date(meta.expires_at).toLocaleString()}`}
              data-testid="edit-tkt-voice-ttl">
              {daysLeft}
            </span>
          )}
        </div>
        <div className="text-[10px] text-[#5C5A55]"
             data-testid="edit-tkt-voice-stats">
          {meta.plays || 0} play(s) · {meta.downloads || 0} download(s)
        </div>
      </div>
      {!blobUrl ? (
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchAudio} disabled={loading}
                  data-testid="edit-tkt-voice-load"
                  className="h-9 px-3 rounded-md bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white text-xs font-semibold inline-flex items-center gap-1.5">
            <Microphone size={12} weight="fill"/>
            {loading ? "Loading…" : "Play voice note"}
          </button>
          <button type="button" onClick={download} disabled={downloading}
                  data-testid="edit-tkt-voice-download"
                  className="h-9 px-3 rounded-md bg-white border border-[#D4E0D1] hover:bg-[#DDECD9] text-[#1A3317] text-xs font-semibold inline-flex items-center gap-1.5">
            <DownloadSimple size={12} weight="bold"/>
            {downloading ? "…" : "Download"}
          </button>
          <span className="text-[10px] text-[#5C5A55]">
            {Math.round((meta.size || 0)/1024)} KB
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <audio controls src={blobUrl}
                  data-testid="edit-tkt-voice-audio"
                  className="flex-1 min-w-0 h-10" autoPlay/>
          <button type="button" onClick={download} disabled={downloading}
                  data-testid="edit-tkt-voice-download"
                  title="Download audio"
                  className="h-9 w-9 rounded-md bg-white border border-[#D4E0D1] hover:bg-[#DDECD9] text-[#1A3317] flex items-center justify-center">
            <DownloadSimple size={14} weight="bold"/>
          </button>
        </div>
      )}
      {error && (
        <div className="mt-1 text-[10px] text-[#7A2E18]">{error}</div>
      )}
    </div>
  );
}

// -------- Timing chips (Iter 110) --------
function TimingChips({ t }) {
  const chips = [];
  if (t.response_label) {
    chips.push({ key: "resp", label: `First response · ${t.response_label}`,
                  bg: "bg-[#F0F7EE] text-[#1A3317] border-[#B5D5AC]" });
  } else if (!t.is_resolved) {
    chips.push({ key: "resp", label: "Awaiting first response",
                  bg: "bg-[#F9E4B7] text-[#7A5A11] border-[#F0DDB2]" });
  }
  if (t.resolution_label && t.is_resolved) {
    chips.push({ key: "resl", label: `Resolved in ${t.resolution_label}`,
                  bg: "bg-[#D4E0D1] text-[#1A3317] border-[#2D5A27]" });
  }
  if (t.delayed_label) {
    const bg = t.is_resolved
      ? "bg-[#E5E2D9] text-[#5C5A55] border-[#E5E2D9]"
      : (t.delayed_seconds >= 24*3600
          ? "bg-[#F4C7B9] text-[#7A2E18] border-[#EFC7B6]"
          : "bg-[#F0EFEA] text-[#5C5A55] border-[#E5E2D9]");
    chips.push({ key: "delay",
      label: t.is_resolved ? `Total ${t.delayed_label}` : `Open for ${t.delayed_label}`,
      bg });
  }
  if (t.assignee_idle_label) {
    chips.push({ key: "idle",
      label: `Assignee idle · ${t.assignee_idle_label}`,
      bg: "bg-[#F4C7B9] text-[#7A2E18] border-[#EFC7B6]" });
  }
  if (chips.length === 0) return null;
  return (
    <div className="mb-4 flex flex-wrap gap-1.5" data-testid="edit-tkt-timing">
      {chips.map(c => (
        <span key={c.key}
              data-testid={`edit-tkt-timing-${c.key}`}
              className={`text-[10px] px-2 py-1 rounded-full border font-medium ${c.bg}`}>
          {c.label}
        </span>
      ))}
    </div>
  );
}

// -------- Activity Timeline (Iter 110) --------
const KIND_META = {
  create:          { color: "bg-[#2D5A27]",  label: "Created" },
  status:          { color: "bg-[#1F3A57]",  label: "Status" },
  priority:        { color: "bg-[#7A5A11]",  label: "Priority" },
  assignee_add:    { color: "bg-[#1A3317]",  label: "Assigned" },
  assignee_remove: { color: "bg-[#8A8780]",  label: "Unassigned" },
  sla_escalate:    { color: "bg-[#8A2C2C]",  label: "SLA" },
  issue:           { color: "bg-[#5C5A55]",  label: "Edited" },
  resolution:      { color: "bg-[#1A3317]",  label: "Resolution" },
};
function ActivityTimeline({ entries }) {
  if (!entries || entries.length === 0) {
    return (
      <div className="border-t border-[#E5E2D9] pt-3">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">
          Activity timeline
        </div>
        <div className="text-[11px] text-[#8A8780] italic">No activity yet.</div>
      </div>
    );
  }
  return (
    <div className="border-t border-[#E5E2D9] pt-3">
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">
        Activity timeline
        <span className="ml-1 text-[9px] normal-case text-[#8A8780]">
          · {entries.length} event(s)
        </span>
      </div>
      <ol className="relative border-l-2 border-[#E5E2D9] ml-2 space-y-2 max-h-64 overflow-y-auto pr-2"
          data-testid="edit-tkt-timeline">
        {entries.map((a, i) => {
          const meta = KIND_META[a.kind] || { color: "bg-[#5C5A55]", label: a.kind };
          return (
            <li key={i}
                data-testid={`edit-tkt-timeline-${i}`}
                className="ml-3 relative">
              <span className={`absolute -left-[17px] top-1 w-2.5 h-2.5 rounded-full border-2 border-white ${meta.color}`}/>
              <div className="text-[11px] flex flex-wrap items-baseline gap-x-1.5">
                <span className="text-[9px] uppercase tracking-[0.14em] font-semibold text-[#5C5A55]">
                  {meta.label}
                </span>
                {a.from != null && a.to != null && (
                  <span className="text-[#1A1A1A]">
                    <span className="text-[#8A8780] line-through">{a.from}</span>
                    <span className="mx-1">→</span>
                    <span className="font-semibold">{a.to}</span>
                  </span>
                )}
                {a.from == null && a.to != null && (
                  <span className="text-[#1A1A1A] font-semibold">{a.to}</span>
                )}
                {a.from != null && a.to == null && a.kind === "assignee_remove" && (
                  <span className="text-[#5C5A55]">Removed <strong>{a.from}</strong></span>
                )}
                {a.note && (
                  <span className="text-[#5C5A55] italic">· {a.note}</span>
                )}
              </div>
              <div className="text-[10px] text-[#8A8780] font-mono">
                {String(a.at || "").replace("T"," ").slice(0,16)}
                <span className="mx-1">·</span>
                by <span className="text-[#5C5A55]">{a.by_name || "system"}</span>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ------ Customer fact chips (Iter 98 · updated Iter 100) ------
function CustomerFactChips({ facts }) {
  const chips = [];
  // Iter 100 — waste-type names come pre-resolved from the backend now
  (facts.primary_waste_types || []).slice(0, 3).forEach(w =>
    chips.push({ key: `w-${w}`, label: w, bg: "bg-[#F0F7EE] text-[#1A3317] border-[#B5D5AC]" })
  );
  if (facts.customer_type) {
    chips.push({ key: "ctype", label: facts.customer_type,
                  bg: "bg-[#DDE7F1] text-[#1F3A57] border-[#1F3A57]" });
  }
  // Iter 100 — Ward chip (ward_name preferred, ward_no fallback)
  const wardLabel = facts.ward_name
    ? `Ward ${facts.ward_no ? facts.ward_no + " · " : ""}${facts.ward_name}`
    : (facts.ward_no ? `Ward ${facts.ward_no}` : null);
  if (wardLabel) {
    chips.push({ key: "ward", label: wardLabel,
                  bg: "bg-[#E8EDF5] text-[#1F3A57] border-[#B7C4DB]" });
  }
  // Iter 100 — Health Zone chip
  if (facts.health_zone) {
    chips.push({ key: "hzone", label: `Health zone · ${facts.health_zone}`,
                  bg: "bg-[#F0E5F0] text-[#5A2E5C] border-[#C7A6C7]" });
  }
  if (facts.last_invoice?.created_at) {
    const dt = facts.last_invoice.created_at.slice(0, 10);
    chips.push({ key: "inv", label: `Last inv ${dt}`,
                  bg: "bg-[#F9E4B7] text-[#7A5A11] border-[#F0DDB2]" });
  } else {
    chips.push({ key: "inv", label: "No invoices yet",
                  bg: "bg-[#E5E2D9] text-[#5C5A55] border-[#E5E2D9]" });
  }
  if (facts.pending_days != null) {
    const bg = facts.pending_days > 14
      ? "bg-[#F4C7B9] text-[#7A2E18] border-[#EFC7B6]"
      : "bg-[#F0EFEA] text-[#5C5A55] border-[#E5E2D9]";
    chips.push({ key: "pending",
      label: `Service pending ${facts.pending_days}d`, bg });
  }
  if (facts.collection_day) {
    chips.push({ key: "sched", label: `Collection · ${facts.collection_day}`,
                  bg: "bg-[#EDF7EA] text-[#1A4E12] border-[#B5D5AC]" });
  }
  if (facts.open_ticket) {
    chips.push({ key: "open-tk",
      label: `Open ticket ${facts.open_ticket.ticket_no}`,
      bg: "bg-[#F4C7B9] text-[#7A2E18] border-[#EFC7B6]" });
  }
  return (
    <div className="flex flex-wrap gap-1" data-testid="tkt-customer-chips">
      {chips.map(c => (
        <span key={c.key}
              data-testid={`tkt-fact-${c.key}`}
              className={`text-[10px] px-2 py-0.5 rounded-full border ${c.bg}`}>
          {c.label}
        </span>
      ))}
    </div>
  );
}

function LookupBanner({ lookup }) {
  if (lookup.state === "idle") return null;
  if (lookup.state === "searching") {
    return <div className="mt-1 text-[11px] text-[#8A8780]">Looking up…</div>;
  }
  if (lookup.state === "found") {
    return (
      <div className="mt-1 text-[11px] text-[#1A3317] bg-[#D4E0D1]/70 rounded px-2 py-1 inline-flex items-center gap-1"
           data-testid="tkt-lookup-found">
        <CheckCircle size={12} weight="fill"/>Existing customer · {lookup.customer.name}
        {lookup.customer.customer_code && <span className="opacity-70">({lookup.customer.customer_code})</span>}
      </div>
    );
  }
  if (lookup.state === "walkin") {
    return (
      <div className="mt-1 text-[11px] text-[#7A5A11] bg-[#F9E4B7]/70 rounded px-2 py-1 inline-flex items-center gap-1"
           data-testid="tkt-lookup-walkin">
        No customer on file — will be logged as <strong>Walk-in</strong>
      </div>
    );
  }
  return null;
}


// -------- AI Analysis Panel (Iter 133) --------
// Read-only summary of what Rigo AI figured out about the ticket:
// priority, tag, sentiment, and an investigation report. Superadmin
// gets an extra 5W root-cause block. `analysis` may be null (never
// fetched), {status:"pending"} (queued), {status:"failed"} (LLM error),
// or a full ready record.
// -------- AI Analysis: recent SR + Invoice activity snapshot (Iter 138) --------
// Renders the compact context Rigo pulled about this customer's recent
// service requests + invoices, so staff see WHO created it, WHO's
// assigned, vehicle number, and timestamps without leaving the ticket.
function RecentActivityBlock({ srs, invoices }) {
  if ((!srs || srs.length === 0) && (!invoices || invoices.length === 0)) {
    return null;
  }
  return (
    <div className="rounded-md bg-white border border-[#DDE7F1] p-2 text-[12px] leading-snug text-[#1A1A1A]"
         data-testid="ai-panel-activity">
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#1F3A57] font-semibold mb-1.5">
        Recent activity Rigo checked
      </div>
      {srs?.length > 0 && (
        <div className="space-y-1.5 mb-2">
          <div className="text-[10px] uppercase tracking-[0.12em] text-[#5C5A55]">Service requests</div>
          {srs.map((sr, i) => (
            <SrRow key={sr.request_no || i} sr={sr}/>
          ))}
        </div>
      )}
      {invoices?.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-[0.12em] text-[#5C5A55]">Invoices</div>
          {invoices.map((inv, i) => (
            <InvRow key={inv.invoice_no || i} inv={inv}/>
          ))}
        </div>
      )}
    </div>
  );
}

function SrRow({ sr }) {
  const created = sr.created_at ? new Date(sr.created_at) : null;
  const assigned = sr.assigned_at ? new Date(sr.assigned_at) : null;
  const fmt = (d) => d ? d.toLocaleString([], {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  }) : "—";
  return (
    <div className="border border-[#EEF2FA] rounded-md px-2 py-1.5 bg-[#F7FAFD]"
         data-testid={`ai-sr-${sr.request_no || ""}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="font-mono font-semibold text-[#1F3A57]">{sr.request_no || "SR—"}</span>
        <span className={`text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded ${
          sr.status === "completed" ? "bg-[#D4E0D1] text-[#2D5A27]" :
          sr.status === "assigned"  ? "bg-[#F9E4B7] text-[#7A5A11]" :
          "bg-[#E5E2D9] text-[#5C5A55]"
        }`}>{sr.status || "—"}</span>
        {sr.priority && sr.priority !== "normal" && (
          <span className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[#F4C7B9] text-[#7A2E18]">
            {sr.priority}
          </span>
        )}
        {sr.service_category && (
          <span className="text-[10px] text-[#5C5A55]">{sr.service_category}</span>
        )}
      </div>
      <div className="mt-0.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-[#4A4740]">
        <div><span className="text-[#8A8780]">Created:</span> {fmt(created)}</div>
        <div><span className="text-[#8A8780]">By:</span> {sr.created_by_name || <em className="text-[#8A8780]">system</em>}</div>
        <div><span className="text-[#8A8780]">Assigned:</span> {fmt(assigned)}</div>
        <div><span className="text-[#8A8780]">Field staff:</span> {sr.assigned_staff_name || <em className="text-[#8A8780]">unassigned</em>}</div>
        {sr.vehicle_number && (
          <div className="col-span-2">
            <span className="text-[#8A8780]">Vehicle:</span>{" "}
            <span className="font-mono">{sr.vehicle_number}</span>
            {sr.vehicle_type && <span className="text-[#8A8780]"> · {sr.vehicle_type}</span>}
            {sr.route_job_no && <span className="text-[#8A8780]"> · route {sr.route_job_no}</span>}
          </div>
        )}
        {(sr.scheduled_date || sr.completed_at) && (
          <div className="col-span-2 text-[10px] text-[#8A8780]">
            {sr.scheduled_date && <>Scheduled {sr.scheduled_date}</>}
            {sr.completed_at && <> · Completed {fmt(new Date(sr.completed_at))}</>}
          </div>
        )}
      </div>
    </div>
  );
}

function InvRow({ inv }) {
  const amount = inv.grand_total ?? inv.total ?? inv.amount ?? 0;
  const created = inv.date || inv.invoice_date || inv.created_at;
  const dt = created ? new Date(created) : null;
  return (
    <div className="border border-[#EEF2FA] rounded-md px-2 py-1.5 bg-[#F7FAFD]"
         data-testid={`ai-inv-${inv.invoice_no || ""}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="font-mono font-semibold text-[#1F3A57]">{inv.invoice_no || "INV—"}</span>
        <span className={`text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded ${
          inv.payment_status === "paid" ? "bg-[#D4E0D1] text-[#2D5A27]" :
          (inv.balance_due || 0) > 0    ? "bg-[#F4C7B9] text-[#7A2E18]" :
          "bg-[#E5E2D9] text-[#5C5A55]"
        }`}>{inv.payment_status || inv.status || "—"}</span>
        <span className="ml-auto font-mono text-[11px]">₹{Number(amount).toFixed(2)}</span>
      </div>
      <div className="mt-0.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-[#4A4740]">
        <div><span className="text-[#8A8780]">Date:</span> {dt ? dt.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" }) : "—"}</div>
        <div><span className="text-[#8A8780]">Created by:</span> {inv.created_by_name || <em className="text-[#8A8780]">system</em>}</div>
        {(inv.balance_due || 0) > 0 && (
          <div className="col-span-2 text-[#7A2E18]">
            Balance due: <span className="font-mono">₹{Number(inv.balance_due).toFixed(2)}</span>
          </div>
        )}
        {inv.field_staff_name && (
          <div className="col-span-2 text-[10px] text-[#8A8780]">
            Field staff: {inv.field_staff_name}
          </div>
        )}
      </div>
    </div>
  );
}



function AIAnalysisPanel({ analysis, isSuperadmin, showSuperReport,
                            setShowSuperReport, onRegenerate, regenerating }) {
  const status = analysis?.status;

  if (!analysis || status === "pending") {
    return (
      <div className="rounded-md border border-[#DDE7F1] bg-[#F4F8FC] p-3 text-[12px] text-[#1F3A57] inline-flex items-center gap-2 w-full"
           data-testid="ai-panel-pending">
        <CircleNotch size={12} className="animate-spin"/>
        Rigo AI is investigating this ticket…
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="rounded-md border border-[#F4C7B9] bg-[#FDECE5] p-3 flex items-center gap-2 text-[12px] text-[#7A2E18]"
           data-testid="ai-panel-failed">
        <Warning size={12}/>Rigo AI couldn&apos;t analyse this one.
        <button type="button" onClick={onRegenerate} disabled={regenerating}
                data-testid="ai-panel-retry"
                className="ml-auto h-7 px-2 rounded-md bg-[#7A2E18] hover:bg-[#5A2013] text-white text-[11px]">
          {regenerating ? "Retrying…" : "Retry"}
        </button>
      </div>
    );
  }

  const priCls = {
    high:   "bg-[#F4C7B9] text-[#7A2E18]",
    medium: "bg-[#F9E4B7] text-[#7A5A11]",
    low:    "bg-[#DDE7F1] text-[#1F3A57]",
  }[analysis.priority] || "bg-[#E5E2D9] text-[#5C5A55]";
  const sentCls = {
    angry:    "bg-[#F4C7B9] text-[#7A2E18]",
    upset:    "bg-[#F9E4B7] text-[#7A5A11]",
    neutral:  "bg-[#E5E2D9] text-[#5C5A55]",
    positive: "bg-[#D4E0D1] text-[#2D5A27]",
  }[analysis.sentiment] || "bg-[#E5E2D9]";

  return (
    <div className="rounded-md border border-[#DDE7F1] bg-[#F4F8FC] p-3 space-y-2"
         data-testid="ai-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkle size={12} weight="fill" className="text-[#1F3A57]"/>
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#1F3A57] font-semibold">
          Rigo AI · Analysis
        </span>
        {analysis.priority && (
          <span className={`text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded ${priCls}`}
                data-testid="ai-panel-priority">
            {analysis.priority}
          </span>
        )}
        {analysis.tag && (
          <span className="text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded bg-white border border-[#DDE7F1] text-[#1F3A57] font-mono"
                data-testid="ai-panel-tag">
            {analysis.tag}
          </span>
        )}
        {analysis.sentiment && (
          <span className={`text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded ${sentCls}`}
                data-testid="ai-panel-sentiment">
            {analysis.sentiment}
          </span>
        )}
        <button type="button" onClick={onRegenerate} disabled={regenerating}
                data-testid="ai-panel-regenerate"
                className="ml-auto text-[10px] text-[#1F3A57] hover:underline disabled:opacity-60">
          {regenerating ? "Regenerating…" : "Regenerate"}
        </button>
      </div>

      {analysis.summary && (
        <div className="text-[13px] text-[#1A1A1A] leading-snug font-medium"
             data-testid="ai-panel-summary">
          {analysis.summary}
        </div>
      )}

      {analysis.investigation_report && (
        <div className="rounded-md bg-white border border-[#DDE7F1] p-2 text-[12px] whitespace-pre-wrap leading-snug text-[#1A1A1A]"
             data-testid="ai-panel-investigation">
          {analysis.investigation_report}
        </div>
      )}

      {/* Iter 138 — Recent SR + Invoice activity snapshot. Renders the
          compact context Rigo used, so staff instantly see who created/
          assigned the SR, which vehicle, and when — without leaving the
          ticket modal. */}
      <RecentActivityBlock srs={analysis.recent_srs || []}
                            invoices={analysis.recent_invoices || []}/>

      {isSuperadmin && analysis.superadmin_report && (
        <>
          <button type="button"
                  onClick={() => setShowSuperReport(!showSuperReport)}
                  data-testid="ai-panel-super-toggle"
                  className="text-[10px] uppercase tracking-[0.16em] text-[#7A2E18] font-semibold inline-flex items-center gap-1">
            {showSuperReport ? <CaretUp size={10}/> : <CaretDown size={10}/>}
            Superadmin · 5W root-cause analysis
          </button>
          {showSuperReport && (
            <div className="rounded-md bg-[#FDECE5] border border-[#F4C7B9] p-2 text-[12px] whitespace-pre-wrap leading-snug text-[#7A2E18] font-mono"
                 data-testid="ai-panel-super-report">
              {analysis.superadmin_report}
            </div>
          )}
        </>
      )}
    </div>
  );
}


// -------- Auto-Reply Toggle (Iter 133) --------
// Sits in the Tickets header. Only admin/superadmin can flip it —
// managers can still see the state (read-only chip).
function AutoReplyToggle() {
  const { user } = useAuth();
  const canToggle = user?.role === "admin" || user?.role === "superadmin";
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/crm-ai-settings")
      .then(r => setEnabled(!!r.data?.auto_reply_enabled))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = async () => {
    if (!canToggle || saving) return;
    setSaving(true);
    try {
      await api.put("/crm-ai-settings", { enabled: !enabled });
      setEnabled(prev => !prev);
      toast.success(`AI auto-reply ${!enabled ? "enabled" : "disabled"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={!canToggle || saving}
      data-testid="ai-auto-reply-toggle"
      title={canToggle
        ? (enabled
            ? "AI auto-reply is ON — click to disable"
            : "AI auto-reply is OFF — click to enable")
        : "Only admin / superadmin can change this"}
      className={`h-10 px-3 rounded-md text-[11px] font-semibold inline-flex items-center gap-1.5 border ${
        enabled
          ? "bg-[#2D5A27] text-white border-[#2D5A27] hover:bg-[#3A7033]"
          : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:bg-[#F7F6F2]"
      } ${!canToggle ? "cursor-not-allowed opacity-80" : ""}`}
    >
      <Robot size={12} weight={enabled ? "fill" : "regular"}/>
      AI auto-reply: <b>{enabled ? "ON" : "OFF"}</b>
    </button>
  );
}


// Iter 141 · Auto-assign toggle — twin of AutoReplyToggle
function AutoAssignToggle() {
  const { user } = useAuth();
  const canToggle = user?.role === "admin" || user?.role === "superadmin";
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/crm-auto-assign-settings")
      .then(r => setEnabled(!!r.data?.enabled))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = async () => {
    if (!canToggle || saving) return;
    setSaving(true);
    try {
      await api.put("/crm-auto-assign-settings", { enabled: !enabled });
      setEnabled(prev => !prev);
      toast.success(`Auto-assign ${!enabled ? "enabled" : "disabled"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={!canToggle || saving}
      data-testid="ai-auto-assign-toggle"
      title={canToggle
        ? (enabled
            ? "New tickets auto-assign by skill — click to disable"
            : "Auto-assign is OFF — click to enable")
        : "Only admin / superadmin can change this"}
      className={`h-10 px-3 rounded-md text-[11px] font-semibold inline-flex items-center gap-1.5 border ${
        enabled
          ? "bg-[#0B2B66] text-white border-[#0B2B66] hover:bg-[#1D3B78]"
          : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:bg-[#F7F6F2]"
      } ${!canToggle ? "cursor-not-allowed opacity-80" : ""}`}
    >
      <Robot size={12} weight={enabled ? "fill" : "regular"}/>
      Auto-assign: <b>{enabled ? "ON" : "OFF"}</b>
    </button>
  );
}

